import { Head } from "@inertiajs/react"
import { useMemo } from "react"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import type { EnneagramType, ProfileData } from "@/types/vocational"
import logo from "@/assets/logo_transparente.png"

interface Props {
  user: {
    id: number
    name: string | null
    email: string
  }
  test_result: {
    nome?: string | null
    email?: string | null
    tipo_eneagrama?: number | null
    area_vocacional?: string | null
    pontuacoes_eneagrama?: Record<string, number> | null
    respostas?: Record<string, number> | null
    fim_at?: string | null
  }
  profiles: ProfileData[]
}

const BRAND = {
  blueDeep: "#102A58",
  blue: "#1A4283",
  orange: "#ED7723",
  yellow: "#F9A646",
  gray: "#696667"
}

function computeCounts(pontuacoes?: Record<string, number> | null, respostas?: Record<string, number> | null) {
  const counts: Record<EnneagramType, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 }

  if (pontuacoes && Object.keys(pontuacoes).length > 0) {
    Object.entries(pontuacoes).forEach(([key, value]) => {
      const type = Number(key) as EnneagramType
      if (type >= 1 && type <= 9) counts[type] = Number(value) || 0
    })
    return counts
  }

  if (respostas && Object.keys(respostas).length > 0) {
    Object.values(respostas).forEach((value) => {
      const type = Number(value) as EnneagramType
      if (type >= 1 && type <= 9) counts[type] += 1
    })
  }

  return counts
}

function computeDominantType(
  counts: Record<EnneagramType, number>,
  forcedType?: number | null
): EnneagramType | null {
  if (forcedType && forcedType >= 1 && forcedType <= 9) return forcedType as EnneagramType

  const sorted = (Object.entries(counts) as [string, number][])
    .map(([type, count]) => [Number(type) as EnneagramType, count] as [EnneagramType, number])
    .sort((a, b) => b[1] - a[1])
  const top = sorted[0]
  return top && top[1] > 0 ? top[0] : null
}

function formatDate(value?: string | null) {
  if (!value) return null
  const date = new Date(value)
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "long" }).format(date)
}

function normalizeList(value?: string[] | string | null) {
  if (!value) return []
  return Array.isArray(value) ? value : value.split(",").map((item) => item.trim()).filter(Boolean)
}

export default function BasicTestResults({ user, test_result, profiles }: Props) {
  const counts = useMemo(
    () => computeCounts(test_result.pontuacoes_eneagrama, test_result.respostas),
    [test_result.pontuacoes_eneagrama, test_result.respostas]
  )
  const totalAnswered = useMemo(() => Object.values(counts).reduce((sum, value) => sum + value, 0), [counts])
  const dominantType = useMemo(
    () => computeDominantType(counts, test_result.tipo_eneagrama),
    [counts, test_result.tipo_eneagrama]
  )

  const profileLookup = useMemo(() => {
    const lookup: Record<number, ProfileData> = {}
    profiles.forEach((p) => { lookup[p.tipo] = p })
    return lookup
  }, [profiles])
  const profileData = dominantType ? profileLookup[dominantType] : null
  const completedAt = formatDate(test_result.fim_at)

  const orderedTypes = Array.from({ length: 9 }, (_, index) => {
    const type = (index + 1) as EnneagramType
    const count = counts[type]
    return {
      type,
      name: profileLookup[type]?.titulo || `Tipo ${type}`,
      count,
      percentage: totalAnswered > 0 ? Math.round((count / totalAnswered) * 100) : 0
    }
  })

  const professions = normalizeList(profileData?.profissoes_indicadas)
  const highlightedProfessions = professions

  if (!dominantType || !profileData) {
    return (
      <>
        <Head title="Resultado Básico" />
        <PageLayout>
          <div className="w-full max-w-2xl bg-white/90 rounded-3xl shadow-xl p-10 text-center">
            <img src={logo} alt="25Segundos" className="h-20 mx-auto mb-6" />
            <h1 className="text-2xl font-bold" style={{ color: BRAND.blueDeep }}>
              Resultado não encontrado
            </h1>
            <p className="mt-4 text-sm text-muted-foreground">
              Não foi possível localizar respostas válidas para o teste básico.
            </p>
          </div>
        </PageLayout>
      </>
    )
  }

  return (
    <>
      <Head title="Resultado Básico" />
      <style>{`
        @media print {
          body { background: #fff !important; }
          .print-hidden { display: none !important; }
          .print-clean { box-shadow: none !important; background: #fff !important; }
          .print-break { break-after: page; page-break-after: always; }
        }
      `}</style>
      <PageLayout>
        <div className="w-full max-w-6xl space-y-8">
          <div className="print-hidden flex items-center justify-end">
            <button
              type="button"
              className="rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition"
              onClick={() => window.print()}
            >
              Imprimir ou salvar em PDF
            </button>
          </div>

          <section className="print-clean bg-white/95 rounded-3xl shadow-xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-4">
                <img src={logo} alt="25Segundos" className="h-20" />
                <div>
                  <p className="text-sm uppercase tracking-[0.2em]" style={{ color: BRAND.gray }}>
                    Orientação Profissional 25Segundos
                  </p>
                  <h1 className="text-3xl md:text-4xl font-bold" style={{ color: BRAND.blueDeep }}>
                    Resultado do Teste Básico
                  </h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    {test_result.nome || user.name || user.email}
                    {completedAt ? ` · Concluído em ${completedAt}` : ""}
                  </p>
                </div>
              </div>
              <div className="rounded-2xl px-6 py-4 text-center" style={{ background: "#F6F7FB" }}>
                <p className="text-xs uppercase tracking-[0.3em]" style={{ color: BRAND.orange }}>
                  Eneatipo Principal
                </p>
                <p className="text-5xl font-bold mt-1" style={{ color: BRAND.blue }}>
                  {dominantType}
                </p>
                <p className="text-sm font-semibold" style={{ color: BRAND.blueDeep }}>
                  {profileData.titulo}
                </p>
              </div>
            </div>

            <div className="mt-10 space-y-6">
              <div className="rounded-2xl border border-[#EDF0F7] p-6">
                <h2 className="text-2xl font-semibold" style={{ color: BRAND.blueDeep }}>
                  Eneagrama: significado
                </h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed text-justify">
                  O Eneagrama é um mapa de personalidade que descreve nove formas de perceber, sentir, pensar e agir no mundo.
                  Ele revela motivações profundas, padrões emocionais e maneiras de lidar com desafios. Não é um rótulo, mas
                  uma ferramenta de autoconhecimento que ajuda a entender por que fazemos o que fazemos. Seu objetivo é ampliar
                  consciência, oferecer clareza sobre escolhas e fortalecer o desenvolvimento humano.
                </p>
              </div>

              <div className="rounded-2xl border border-[#EDF0F7] p-6">
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
                  Seu Talento Natural
                </p>
                <h2 className="text-2xl font-semibold mt-2" style={{ color: BRAND.blueDeep }}>
                  {profileData.talent}
                </h2>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed text-justify">
                  {profileData.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-2xl border border-[#EDF0F7] p-5 min-h-[220px]">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: BRAND.blue }}>
                    Pontos fortes
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed text-justify">
                    {profileData?.pontos_fortes || "Use este resultado para reconhecer seus principais pontos de força."}
                  </p>
                </div>
                <div className="rounded-2xl border border-[#EDF0F7] p-5 min-h-[220px]">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: BRAND.blue }}>
                    Pontos a desenvolver
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed text-justify">
                    {profileData?.pontos_fracos || "Use este resultado para lapidar seus pontos de crescimento."}
                  </p>
                </div>
                <div className="rounded-2xl border border-[#EDF0F7] p-5 min-h-[220px]">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.25em]" style={{ color: BRAND.blue }}>
                    Estilos profissionais
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed text-justify">
                    {profileData?.estilos_profissionais || "Observe quais estilos de trabalho combinam melhor com seu perfil."}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="print-clean bg-white/95 rounded-3xl shadow-xl p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
              Profissões em destaque
            </p>
            <h2 className="text-2xl font-semibold mt-2" style={{ color: BRAND.blueDeep }}>
              Caminhos profissionais para o seu perfil
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Use estas referências como ponto de partida para explorar áreas que combinam com seus talentos e motivações.
            </p>
            <div className="mt-5 rounded-2xl border border-[#EDF0F7] p-5 bg-white">
              <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
                Área Vocacional
              </p>
              <h3 className="text-xl font-semibold mt-2" style={{ color: BRAND.blueDeep }}>
                {test_result.area_vocacional || profileData.vocationalArea || "Multidisciplinar"}
              </h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {profileData.style}
              </p>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              {highlightedProfessions.slice(0, 8).map((item) => (
                <div
                  key={item}
                  className="rounded-xl border border-[#EDF0F7] bg-[#FFF7ED] px-4 py-3 text-sm font-semibold"
                  style={{ color: BRAND.blueDeep }}
                >
                  {item}
                </div>
              ))}
              {highlightedProfessions.length === 0 && (
                <div className="rounded-xl border border-[#EDF0F7] px-4 py-3 text-sm text-muted-foreground">
                  Nenhuma profissão específica cadastrada para este perfil no momento.
                </div>
              )}
            </div>
          </section>

          <section className="print-clean bg-white/95 rounded-3xl shadow-xl p-8 md:p-10">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.orange }}>
                  Distribuição dos Tipos
                </p>
                <h2 className="text-2xl font-semibold" style={{ color: BRAND.blueDeep }}>
                  Seus nove pontos de equilíbrio
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">
                {totalAnswered} respostas contabilizadas
              </p>
            </div>

            <div className="mt-8 space-y-4">
              {orderedTypes.map((entry) => (
                <div key={entry.type} className="rounded-2xl border border-[#EDF0F7] p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.25em]" style={{ color: BRAND.gray }}>
                        Tipo {entry.type}
                      </p>
                      <h3 className="text-lg font-semibold" style={{ color: BRAND.blueDeep }}>
                        {entry.name}
                      </h3>
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold" style={{ color: BRAND.blue }}>
                        {entry.percentage}%
                      </span>
                      <p className="text-xs text-muted-foreground">
                        {entry.count} respostas
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${entry.percentage}%`,
                        background: entry.type === dominantType ? BRAND.orange : BRAND.blue
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </PageLayout>
    </>
  )
}
